/**
 * Newsletter HTML helpers — Copy to clipboard + Download .html file.
 *
 * Both pull from `GET /api/newsletters/{id}/source.html` — the same
 * endpoint Tanisha gets in the bulk-handover Resend attachment, so
 * what the dashboard shows = what Tanisha pastes into Zoho.
 *
 * Always rendered WITHOUT the [DRAFT] ribbon and WITHOUT Soul-Form
 * personalisation (bulk send goes to the whole list).
 */
import api from '@/lib/api';
import { toast } from 'sonner';

/**
 * Fetch the bulk-send HTML source for a newsletter.
 * Returns the raw HTML string.
 */
export async function fetchNewsletterHtml(newsletterId) {
  const res = await api.get(`/newsletters/${newsletterId}/source.html`, {
    responseType: 'text',
    transformResponse: [(d) => d],  // axios normally JSON-parses; we want raw text
    headers: { Accept: 'text/html' },
  });
  return typeof res.data === 'string' ? res.data : String(res.data ?? '');
}

/**
 * Copy the rendered HTML to the user's clipboard.
 * Toasts success/failure. Returns boolean ok.
 */
export async function copyNewsletterHtml(newsletterId) {
  try {
    const html = await fetchNewsletterHtml(newsletterId);
    if (!html) {
      toast.error('No HTML — newsletter may be empty or unsaved.');
      return false;
    }
    if (!navigator.clipboard?.writeText) {
      // Fallback: legacy textarea-select-execCommand path for older WebViews.
      const ta = document.createElement('textarea');
      ta.value = html;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      ta.remove();
      if (!ok) throw new Error('Clipboard not available in this browser.');
    } else {
      await navigator.clipboard.writeText(html);
    }
    toast.success(`Copied ${(html.length / 1024).toFixed(1)} KB of HTML — paste into Zoho.`);
    return true;
  } catch (e) {
    toast.error(`Copy failed: ${e?.message || 'unknown error'}`);
    return false;
  }
}

/**
 * Trigger a browser download of the rendered HTML as a `.html` file.
 * Filename: `<client>-<year>-<month>-newsletter.html`.
 */
export async function downloadNewsletterHtml(newsletterId, filenameHint) {
  try {
    const html = await fetchNewsletterHtml(newsletterId);
    if (!html) {
      toast.error('No HTML — newsletter may be empty or unsaved.');
      return false;
    }
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filenameHint || `newsletter-${newsletterId}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    toast.success('HTML file downloaded — open in editor & paste into Zoho.');
    return true;
  } catch (e) {
    toast.error(`Download failed: ${e?.message || 'unknown error'}`);
    return false;
  }
}
